









import { Component, Prop, Vue } from 'vue-property-decorator';
import { AxiosResponse } from 'axios';
import moment from 'moment';

import { WaterApi, WaterTotalizerReading, WaterTotalizerReadingDynamoItem } from '@/api';
import { Constants } from '@/common/Constants';
import { isWaterMeterEntity } from '@/common/Validators';
import { ApiUtils } from '@/common/ApiUtils';
import { Legend, Modal, percent, Root, Tooltip } from '@amcharts/amcharts5';
import { XYChart } from '@amcharts/amcharts5/.internal/charts/xy/XYChart';
import { DateAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/DateAxis';
import { AxisRenderer } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRenderer';
import { AxisRendererX } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererX';
import { ValueAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/ValueAxis';
import { AxisRendererY } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererY';
import { XYCursor } from '@amcharts/amcharts5/.internal/charts/xy/XYCursor';
import { LineSeries } from '@amcharts/amcharts5/.internal/charts/xy/series/LineSeries';
import { DateRange } from '@/models/date-range';

import Toast from '@/components/display/toast.vue';
import DatePicker from '@/components/form pickers/DatePicker.vue';

@Component({
	components: {
		DatePicker,
	},
})
export default class TotalizerChart extends Vue {
	@Prop({ required: true, type: Number })
	private entityId!: number;

	@Prop({ required: true, type: String, validator: isWaterMeterEntity })
	private entityType!: string;

	public totalizerReadings: WaterTotalizerReading[] | null = null;
	public loading: boolean = true;

	private startTime: number = Math.floor(
		moment()
			.subtract(14, 'days')
			.valueOf() / 1000,
	);
	private finishTime: number = Math.floor(moment().valueOf() / 1000);
	private toast: Toast = new Toast();
	private series: LineSeries | null = null;
	private noDataModal: Modal | null = null;
	private xAxis: DateAxis<AxisRenderer> | null = null;

	private get hasData(): boolean {
		return !!(this.totalizerReadings && this.totalizerReadings.length > 0);
	}

	public async created(): Promise<void> {
		this.totalizerReadings = await this.fetchTotalizerReadings();
		if (this.totalizerReadings) {
			await Vue.nextTick();
			this.createChart(this.totalizerReadings);
			if (!this.hasData) {
				this.noDataModal?.open();
			}
		}
	}

	public async onDateRangeChanged(newDateRange: Omit<DateRange, 'shortcut'>): Promise<void> {
		this.startTime = Math.floor(moment(newDateRange.start, 'DD-MM-YYYY').valueOf() / 1000);
		this.finishTime = Math.floor(moment(newDateRange.end, 'DD-MM-YYYY').valueOf() / 1000);
		this.totalizerReadings = await this.fetchTotalizerReadings();
		if (this.totalizerReadings) {
			this.series?.data.setAll(
				this.totalizerReadings.map(x => ({ ...x, timestamp: x.timestamp * 1000 })),
			);
			if (!this.hasData) {
				this.noDataModal?.open();
			} else {
				this.noDataModal?.close();
			}
			this.xAxis?.setAll({ min: this.startTime * 1000, max: this.finishTime * 1000 });
		}
	}

	private async fetchTotalizerReadings(): Promise<WaterTotalizerReading[] | null> {
		try {
			const totalizerReadingsResponse = await this.totalizerReadingsApiCall(
				this.entityId,
				this.startTime,
				this.finishTime,
			);
			return totalizerReadingsResponse.data.map(
				(dynamoItem: WaterTotalizerReadingDynamoItem) => dynamoItem.data,
			);
		} catch (e) {
			const errorResponse = ApiUtils.handleApiError(e, 'Failed to fetch totalizer readings');
			this.toast.makeToast(errorResponse.errorMessage, 'Error', 'danger');
			return null;
		} finally {
			this.loading = false;
		}
	}

	private createChart(chartData: WaterTotalizerReading[]): void {
		const root: Root = Root.new('totalizerChart');
		const chart: XYChart = XYChart.new(root, {});
		const { cursor, xAxis, yAxis, series, legend, noDataModal } = this.createChartFeatures(root);
		this.series = series;
		this.noDataModal = noDataModal;
		this.xAxis = xAxis;
		chart.set('cursor', cursor);
		chart.xAxes.push(xAxis);
		chart.yAxes.push(yAxis);
		chart.series.push(series);
		chart.bottomAxesContainer.children.push(legend);
		series.data.setAll(chartData.map(x => ({ ...x, timestamp: x.timestamp * 1000 })));
		legend.data.setAll(chart.series.values);
		root.container.children.push(chart);
		series.appear(1000);
		chart.appear(1000, 100);
	}

	private createChartFeatures(
		root: Root,
	): {
		xAxis: DateAxis<AxisRenderer>;
		yAxis: ValueAxis<AxisRenderer>;
		cursor: XYCursor;
		series: LineSeries;
		legend: Legend;
		noDataModal: Modal;
	} {
		const xAxis: DateAxis<AxisRenderer> = DateAxis.new(root, {
			baseInterval: { timeUnit: 'minute', count: 15 },
			renderer: AxisRendererX.new(root, {}),
			tooltip: Tooltip.new(root, {}),
			min: this.startTime * 1000,
			max: this.finishTime * 1000,
		});
		const yAxis: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, {}),
			min: 0,
		});
		const cursor: XYCursor = XYCursor.new(root, { behavior: 'none' });
		const series: LineSeries = LineSeries.new(root, {
			name: 'Totalizer',
			xAxis,
			yAxis,
			tooltip: Tooltip.new(root, {
				labelText: '{valueY}L',
			}),
			showTooltipOn: 'always',
			valueXField: 'timestamp',
			valueYField: 'totalUsage',
		});
		series.strokes.template.setAll({
			strokeWidth: 2,
		});
		const legend: Legend = Legend.new(root, {
			x: percent(50),
			centerX: percent(50),
		});
		const noDataModal: Modal = Modal.new(root, {
			content: `
        <div class="row justify-content-center">
          <div class="container col justify-content-center">
            <div>No totalizer data for this water entity, over this range</div>
          </div>
        </div>
      `,
		});
		return {
			xAxis,
			yAxis,
			cursor,
			series,
			legend,
			noDataModal,
		};
	}

	private get totalizerReadingsApiCall(): (
		entityId: number,
		startTime: number,
		finishTime: number,
	) => Promise<AxiosResponse<WaterTotalizerReadingDynamoItem[]>> {
		if (this.entityType === Constants.ENTITY_TYPE_WATER_MONITOR) {
			return new WaterApi().getMonitorTotalizerReadings;
		}
		return new WaterApi().getMeterTotalizerReadings;
	}
}
